import type { Exercise } from "./schemas/exercise.js";

// Ranked alternative for a swap. Score is opaque (only ordering matters);
// the breakdown fields stay on the candidate so the UI can surface a "why
// this matches" hint without rerunning the math.
export type SwapCandidate = {
  exercise: Exercise;
  score: number;
  tagOverlap: number;
  primaryOverlap: number;
  samePattern: boolean;
  sameEquipment: boolean;
};

const primarySet = (ex: Exercise): Set<string> => {
  if (ex.primary && ex.primary.length > 0) return new Set(ex.primary);
  if (ex.primaryMuscle) return new Set([ex.primaryMuscle]);
  return new Set();
};

const intersectSize = (a: Set<string>, b: Set<string>): number => {
  let n = 0;
  for (const x of a) if (b.has(x)) n++;
  return n;
};

// Rank catalog exercises against `current` so the workout swap picker can
// suggest alternatives when the bench is taken or a station is occupied.
// Tag overlap dominates because tags carry the coach's intent (push,
// horizontal, hypertrophy…); structural fields are tiebreakers. Equipment
// is biased toward *different* on purpose — the dominant use case is
// "panchina occupata → DB bench", so giving same-equipment candidates a
// small penalty surfaces the substitute the user actually came looking for.
//
// Candidates with zero relation (no shared tag, no shared primary muscle)
// are omitted; they aren't useful suggestions and would only dilute the
// list. The caller can fall back to the full catalog when the rank is
// empty (legacy rows with no tags + no primary[]).
export function rankSwapCandidates(
  current: Exercise,
  catalog: readonly Exercise[],
): SwapCandidate[] {
  const currentTags = new Set(current.tags ?? []);
  const currentPrimary = primarySet(current);
  const currentMP = current.movementPattern;
  const currentEq = current.equipment;

  const ranked: SwapCandidate[] = [];
  for (const ex of catalog) {
    if (ex.slug === current.slug) continue;
    const tagOverlap = intersectSize(currentTags, new Set(ex.tags ?? []));
    const primaryOverlap = intersectSize(currentPrimary, primarySet(ex));
    if (tagOverlap === 0 && primaryOverlap === 0) continue;
    const samePattern = currentMP !== undefined && ex.movementPattern === currentMP;
    const sameEquipment = currentEq !== undefined && ex.equipment === currentEq;
    const score =
      tagOverlap * 3 +
      primaryOverlap * 2 +
      (samePattern ? 1 : 0) +
      (sameEquipment ? 0 : 0.5);
    ranked.push({
      exercise: ex,
      score,
      tagOverlap,
      primaryOverlap,
      samePattern,
      sameEquipment,
    });
  }
  ranked.sort(
    (a, b) =>
      b.score - a.score ||
      a.exercise.name.en.localeCompare(b.exercise.name.en),
  );
  return ranked;
}
